import type { PreviewDemo } from "@innoclouds/ui";
import { find, first, hasText, isOpen, sleep, tap, visibleFirst, waitFor } from "./dom";

export const dismissStart = async () => {
	const seen = await waitFor(() => document.querySelector<HTMLElement>(".start_page") ?? (isOpen("main") ? document.body : null));
	if (!seen || seen === document.body) return;
	seen.click();
	await sleep(800);
};

const normalize = (text: string) => text.replace(/\s+/g, " ").trim().toLowerCase();

const cardOf = (subject: string) =>
	visibleFirst(
		find("main", "card").filter((card) => {
			const title = card.querySelector<HTMLElement>('[data-el="card_title"]')?.innerText;
			return title ? normalize(title) === normalize(subject) : hasText(card, subject);
		}),
	);

const railItems = () => find("main", "rail_item").filter((item) => !hasText(item, "♥"));

export const findCard = async (subject: string, { sections }: PreviewDemo) => {
	const shown = cardOf(subject);
	if (shown) return shown;
	await waitFor(() => isOpen("main"));
	const section = sections?.[subject] && normalize(sections[subject]);
	const name = (item: HTMLElement) => normalize(item.innerText);
	const visited = new Set<string>();

	for (let step = 0; step < 60; step++) {
		const rail = railItems();
		const own = section ? rail.find((item) => name(item) === section && !visited.has(section)) : undefined;
		if (own) {
			visited.add(section as string);
			tap(own);
			const found = await waitFor(() => cardOf(subject), 2000);
			if (found) return found;
			continue;
		}
		if (section && !visited.has(section)) {
			const last = rail.at(-1);
			if (!last || visited.has(`jump:${name(last)}`)) visited.add(section);
			else {
				visited.add(`jump:${name(last)}`);
				tap(last);
				await sleep(500);
			}
			continue;
		}
		const next = rail.find((item) => !visited.has(name(item)));
		if (!next) return null;
		visited.add(name(next));
		tap(next);
		const found = await waitFor(() => cardOf(subject), 1200);
		if (found) return found;
	}
	return null;
};

export const openProduct = async (subject: string, demo: PreviewDemo) => {
	const card = await findCard(subject, demo);
	card?.querySelector<HTMLElement>('[data-el="card_inner"]')?.click();
	return waitFor(() => isOpen("product_card"));
};

export const buy = async () => tap(await waitFor(() => first("product_card", "buy_button")));

export const addToCart = async (subject: string, demo: PreviewDemo) => {
	if (!(await openProduct(subject, demo))) return;
	await buy();
	await waitFor(() => (isOpen("product_card") ? null : true), 3000);
	await sleep(300);
};
